import { Check, CircleAlert, LockKeyhole, Rocket, ShieldCheck } from "lucide-react";
import { Badge } from "../../lib/ui";
import DependencyGraph from "./DependencyGraph";

export function DeliveryValuePanel({ app, g8Closed, value }) {
  const vr = app.value_realization || {};
  const headline = vr.headline && vr.headline !== "—" ? vr.headline : null;
  return (
    <section className="feature-panel value-panel" data-testid="delivery-focus-value">
      <p className="kicker">VALUE · EXPECTED VS ACTUAL</p>
      <h2>Did we achieve the promised WHY?</h2>
      {headline && (
        <div className="roi-callout value-headline" data-testid="value-realization-headline">
          <strong>{headline}</strong>
          <span>{vr.proved ? "G8 proved" : vr.g8_state === "PASS" ? "closed" : "in progress"}</span>
        </div>
      )}
      <p className="muted small-note">Beginning WHY: <b>{app.why.driver}</b> — {app.why.problem}</p>
      <p className="muted small-note" data-testid="value-codn"><strong>CoDN:</strong> {value.codn || app.why?.risk_of_nothing || "Unknown — not treated as zero"}</p>
      <div className="value-bars">
        <div><span>Baseline TCO</span><b style={{ width: "100%" }} /><strong>{value.baseline_tco}</strong></div>
        <div><span>Target TCO</span><b style={{ width: "57%" }} /><strong>{value.target_tco}</strong></div>
        <div><span>Actual TCO</span><b className={g8Closed ? "" : "actual-bar"} style={{ width: g8Closed ? "57%" : "3%" }} /><strong>{value.actual_tco || "—"}</strong></div>
      </div>
      <div className="roi-callout" data-testid="value-roi">
        <strong>{g8Closed && value.actual_roi ? `${value.actual_roi}%` : `Forecast ${value.expected_roi}%`}</strong>
        <span>{g8Closed ? "actual ROI" : "expected ROI"}</span>
      </div>
      {value.variance_cause && (
        <p className="muted small-note" data-testid="value-variance-cause"><strong>Variance cause:</strong> {value.variance_cause}</p>
      )}
      <div className="kpi-compare" data-testid="kpi-compare">
        <div className="kpi-col">
          <h4>BEFORE</h4>
          <div><small>Deploy freq</small><b>{value.before.deploy_freq}</b></div>
          <div><small>Change failure</small><b>{value.before.cfr}</b></div>
          <div><small>Availability</small><b>{value.before.availability}</b></div>
          <div><small>Unit cost</small><b>{value.before.unit_cost}</b></div>
        </div>
        <div className="kpi-arrow">→</div>
        <div className="kpi-col">
          <h4>AFTER</h4>
          {value.after ? (
            <>
              <div><small>Deploy freq</small><b className="green-text">{value.after.deploy_freq}</b></div>
              <div><small>Change failure</small><b className="green-text">{value.after.cfr}</b></div>
              <div><small>Availability</small><b className="green-text">{value.after.availability}</b></div>
              <div><small>Unit cost</small><b className="green-text">{value.after.unit_cost}</b></div>
            </>
          ) : (
            <p className="muted small-note">Recorded after production success (G8).</p>
          )}
        </div>
      </div>
      <p className="muted small-note">L4 cutover and G8 evidence lives on Evidence Intake. UNKNOWN is never coerced.</p>
    </section>
  );
}

export function DeliveryGatesPanel({
  app,
  authorized,
  deployed,
  g8Closed,
  dep,
  wave,
  pendingSignoff,
  maySign,
  canDeploy,
  highlightGate,
  onSignoff,
  onDeploy,
  onCloseG8,
}) {
  const focusGate = highlightGate && ["G5", "G6", "G7", "G8"].includes(highlightGate) ? highlightGate : null;
  return (
    <section className="feature-panel delivery-panel" data-testid="delivery-focus-gates">
      {focusGate && (
        <div className="deployment-note action" data-testid={`delivery-queue-focus-${focusGate.toLowerCase()}`}>
          <LockKeyhole size={16} />
          <div>
            <strong>Queue item: {focusGate} needs your attention on {app.id}</strong>
            <p>Open sign-off or validation for this gate — not a generic delivery overview.</p>
            {maySign && pendingSignoff.includes(focusGate) && (
              <button type="button" className="secondary-button small-btn" data-testid={`delivery-focus-signoff-${focusGate.toLowerCase()}`} onClick={() => onSignoff(focusGate)}>
                <ShieldCheck size={14} /> Sign off {focusGate}
              </button>
            )}
          </div>
        </div>
      )}
      <div className="panel-header">
        <div>
          <p className="kicker">G3–G8 · {wave.id || "WAVE"}</p>
          <h2>Gate sign-off & production GO</h2>
          <p className="muted">Owner: <b>{wave.owner}</b> · Target: {wave.target}</p>
        </div>
        <Badge tone={g8Closed ? "green" : deployed ? "amber" : "cyan"}>{(dep.state || "NOT_SCHEDULED").replaceAll("_", " ")}</Badge>
      </div>
      {!authorized && (
        <div className="deployment-note">
          <CircleAlert size={16} />
          <div><strong>MDR must be authorized before GO evidence is accepted</strong><p>Human authorization gates the GO record. Advance via the 7R Decision page.</p></div>
        </div>
      )}
      {maySign && authorized && !deployed && pendingSignoff.length > 0 && (
        <div className="deployment-note action" data-testid="delivery-gate-signoff">
          <LockKeyhole size={16} />
          <div>
            <strong>G3–G6 need authority sign-off before deployment</strong>
            <p>G7 stays LOCKED until G5 & G6 are PASS.</p>
            <div className="signoff-row">
              {pendingSignoff.map((id) => (
                <button key={id} type="button" data-testid={`delivery-signoff-${id.toLowerCase()}`} className="secondary-button small-btn" onClick={() => onSignoff(id)}>
                  <ShieldCheck size={14} /> Sign off {id}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {maySign && authorized && !deployed && pendingSignoff.length === 0 && (
        <div className="deployment-note action" data-testid="delivery-ready-to-deploy">
          <Rocket size={16} />
          <div>
            <strong>G3–G6 confirmed · production GO available</strong>
            <p data-testid="g7-owner-cue">G7 owner is Elena Ruiz · Business Owner. Actuals populate at G8 closure, not on deploy.</p>
          </div>
          <button data-testid="delivery-run-deploy" className="primary-button" type="button" onClick={onDeploy} disabled={!canDeploy}><Rocket size={15} /> Run deployment</button>
        </div>
      )}
      {maySign && deployed && !g8Closed && (
        <div className="deployment-note action" data-testid="delivery-pending-validation">
          <ShieldCheck size={16} />
          <div>
            <strong>Deployment recorded · production validation PENDING</strong>
            <p>{dep.health}. G8 requires business validation — deployment alone is not success.</p>
            <p className="muted small-note" data-testid="g8-owner-cue">G8 owner is Elena Ruiz · Business Owner. Close G8 with expected vs actual value.</p>
          </div>
          <button data-testid="delivery-close-g8" className="primary-button" type="button" onClick={onCloseG8}><Check size={15} /> Provide business validation & close G8</button>
        </div>
      )}
      {g8Closed && (
        <div className="deployment-note success">
          <Check size={16} />
          <div><strong>Production success confirmed</strong><p>Benefit realization tracks against the original WHY.</p></div>
        </div>
      )}
    </section>
  );
}

export function DeliveryTrackPanel({ app, authorized, deployed, g8Closed, dep, wave, gates }) {
  return (
    <div className="delivery-grid" data-testid="delivery-focus-track">
      <section className="feature-panel delivery-panel">
        <div className="panel-header">
          <div>
            <p className="kicker">{wave.id || "WAVE"} · {wave.name}</p>
            <h2>Delivery track · G1–G8 unchanged</h2>
            <p className="muted">Owner: <b>{wave.owner}</b> · Target: {wave.target}</p>
          </div>
          <Badge tone={g8Closed ? "green" : deployed ? "amber" : "cyan"}>{(dep.state || "NOT_SCHEDULED").replaceAll("_", " ")}</Badge>
        </div>
        <div className="delivery-steps">
          {[
            ["MDR", authorized ? "Authorized" : "Pending", authorized],
            ["Wave", wave.id ? "Planned" : "Not planned", !!wave.id],
            ["G5", "Security", gates.find((g) => g.id === "G5")?.state === "PASS"],
            ["G6", "Test & cutover", gates.find((g) => g.id === "G6")?.state === "PASS"],
            ["G7", "Production GO", gates.find((g) => g.id === "G7")?.state === "PASS"],
            ["Deploy", (dep.state || "NOT_SCHEDULED").replaceAll("_", " "), deployed],
            ["Validate", dep.validation, dep.validation === "PASSED"],
            ["Hypercare", (dep.hypercare || "NOT_STARTED").replaceAll("_", " "), dep.hypercare === "IN_PROGRESS" || dep.hypercare === "CLOSED"],
            ["G8", "Success", g8Closed],
          ].map(([label, sub, done], i) => (
            <div
              key={label}
              className={done ? "delivery-step done" : (label === "Deploy" && authorized && !deployed) ? "delivery-step current" : "delivery-step"}
              data-testid={`delivery-step-${label.toLowerCase()}`}
            >
              <span>{done ? <Check size={13} /> : i + 1}</span>
              <strong>{label}</strong>
              <p>{sub}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function DeliveryPackagesPanel({ app }) {
  const packages = app.work_packages || app.brd?.work_packages || [];
  const goPkg = app.go_package || app.brd?.go_package;
  return (
    <div data-testid="delivery-focus-packages">
      {packages.length > 0 && (
        <section className="feature-panel" data-testid="work-packages">
          <p className="kicker">WAVE / WORK PACKAGES · LEAN TRACKING</p>
          <h2>Owner, status, blockers — not a PM suite</h2>
          {packages.map((wp) => (
            <div key={wp.id} className="wp-row">
              <code>{wp.id}</code>
              <strong>{wp.name}</strong>
              <span>{wp.owner}</span>
              <Badge tone={wp.status === "BLOCKED" ? "rose" : "cyan"}>{wp.status}</Badge>
              {wp.blocker && <small>{wp.blocker}</small>}
            </div>
          ))}
        </section>
      )}
      {goPkg && (
        <section className="feature-panel" data-testid="go-package">
          <p className="kicker">G7 · CUTOVER + ROLLBACK + NAMED OWNER</p>
          <h2>Deploy GO package</h2>
          {goPkg.seeded_preview && (
            <p className="muted small-note" data-testid="go-seeded-preview">Seeded preview — MDR not authorized. Live GO still requires G5 + G6 and deploy POST.</p>
          )}
          <div className="go-package-grid">
            <div><small>CUTOVER</small><p>{goPkg.cutover_plan}</p></div>
            <div><small>ROLLBACK</small><p>{goPkg.rollback_plan}</p></div>
            <div><small>GO OWNER</small><p>{goPkg.go_owner}</p></div>
          </div>
        </section>
      )}
      {!packages.length && !goPkg && (
        <section className="feature-panel">
          <p className="muted">No wave packages seeded for this application yet.</p>
        </section>
      )}
    </div>
  );
}

export function DeliveryDependencyPanel({ app }) {
  const deps = app.dependencies || { nodes: [], edges: [] };
  return (
    <section className="feature-panel dependency-panel" data-testid="delivery-focus-dependencies">
      <div className="panel-header">
        <div>
          <p className="kicker">DEPENDENCY MAP · IMPACT-AWARE SEQUENCING</p>
          <h2>Wave feasibility · at a glance</h2>
          <p className="muted small-note">Click any node to see affected applications, waves, and blocked gates.</p>
        </div>
        <Badge tone={deps.nodes.length ? "cyan" : "slate"}>{deps.nodes.length} mapped</Badge>
      </div>
      {deps.nodes.length > 0 ? (
        <DependencyGraph deps={deps} />
      ) : (
        <p className="muted small-note">No dependency graph seeded for this application.</p>
      )}
    </section>
  );
}
